import type { Item, Locale, PlanChanges, PlanDay as PlanDayType } from '@itin/shared'
import { safeUrl, zoneCityLabel } from '@itin/shared'
import { KIND_GLYPH } from './ItemForm.js'
import { Markdown } from './Markdown.js'
import { ChangeBadge } from './PlanUpdates.js'
import { useLocale, useT } from '../lib/i18n.js'

/**
 * One day of the itinerary, as the traveller reads it.
 *
 * A single column in the order things happen: time, what it is, and then only
 * what that particular entry needs. What it replaced showed places and bookings
 * as two separate lists, so one visit to Versailles could appear twice — once
 * with its address and once with its ticket time — and the reader had to
 * assemble them. Now it is one line.
 *
 * Shared by the share page and, later, the print route, so what is printed is
 * what was read. Two units rendering "the same" day differently is how a
 * confirmation number goes missing from paper.
 */

/** A constructed URL. No API key, no SDK, independent of the map provider. */
function mapsHref(item: Item): string | null {
  if (item.lat !== null && item.lng !== null) {
    return `https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lng}`
  }
  const address = item.addressLocal ?? item.address
  return address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null
}

/** The calendar date an instant falls on, in a given zone. `en-CA` is ISO. */
function dateInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(new Date(iso))
}

/** Times render in the zone the event happens in — not the reader's. */
function formatTime(iso: string, timeZone: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(new Date(iso))
}

function formatDayHeading(isoDate: string, locale: Locale): string {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number]
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function ItemEntry({
  item,
  timeZone,
  changes,
}: {
  item: Item
  timeZone: string
  /**
   * What has moved since this reader last looked. Optional, and absent means
   * "do not mark anything" rather than "nothing changed" — the print route and
   * anything else rendering a day for a reader with no baseline should get an
   * unmarked page, not one asserting that nothing happened.
   */
  changes?: PlanChanges | undefined
}) {
  const t = useT()
  const { locale } = useLocale()
  const change = changes?.items.get(item.id)

  // The item's own zone wins where it has one. Null means "the trip's", which
  // is right for hotels, restaurants and museums — everything that happens
  // where the trip is.
  const zone = item.timezone ?? timeZone
  const elsewhere = zone !== timeZone

  /**
   * Where it ENDS. Almost always the same place; for a flight, not.
   *
   * This line used to read `formatTime(item.endsAt, zone, ...)` — the arrival
   * on the DEPARTURE clock, which for GRU→CDG is five hours wrong. It looked
   * right because it was consistent, and it was consistent because it was one
   * variable doing two jobs.
   */
  const endZone = item.endTimezone ?? zone
  const landsElsewhere = endZone !== zone

  /** Whether the arrival falls on a later calendar date, on its own clock. */
  const landsNextDay =
    item.startsAt !== null &&
    item.endsAt !== null &&
    dateInZone(item.startsAt, zone) !== dateInZone(item.endsAt, endZone)
  const maps = mapsHref(item)
  const website = safeUrl(item.website)

  return (
    <li className="flex gap-3 py-2">
      {/* The time column is fixed width so the whole day reads as a timeline
          rather than as a ragged list. Entries with no time keep their place
          in the column and simply say so. */}
      {/* Fixed width so the day reads as a timeline rather than a ragged list.
          Entries with no time keep their place in the column. */}
      <div className="w-12 shrink-0 pt-0.5 text-right">
        {item.startsAt ? (
          <span className="text-sm font-semibold tabular-nums text-slate-900">
            {formatTime(item.startsAt, zone, locale)}
          </span>
        ) : (
          <span aria-hidden="true" className="text-sm text-slate-300">
            ·
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {/*
          Title first, on one line with its glyph. The kind used to get its own
          letterspaced caps line above every entry — nine days of that reads as
          filing, not as an itinerary, and the glyph already says "flight".
        */}
        <p className="flex flex-wrap items-baseline gap-x-2 leading-snug">
          <span aria-hidden="true" className="text-slate-400">
            {KIND_GLYPH[item.kind]}
          </span>
          <span className="text-base font-medium text-slate-900">{item.title}</span>
          {/*
            Beside the title, before the times.

            This badge is about the WHOLE entry — a rebooked flight changes its
            hour, a swapped restaurant changes its name and address — so it sits
            with the thing it qualifies rather than after the schedule line,
            where it would read as though only the time had moved.
          */}
          {change && <ChangeBadge kind={change} />}
          {/*
            Before the arrival, not after it.

            This badge qualifies the DEPARTURE time. Rendered at the end of the
            line it landed after "– 13:30 Paris", where it reads as though São
            Paulo were the arrival's clock — the precise confusion the two-zone
            model exists to remove.
          */}
          {elsewhere && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">
              {t('plan.localTimeIn', { place: zoneCityLabel(zone, locale) })}
            </span>
          )}
          {item.endsAt && (
            <span className="text-sm text-slate-500">
              – {formatTime(item.endsAt, endZone, locale)}
              {/*
                Named, when it is a different clock. "07:05" against a São Paulo
                departure is the number somebody plans a pickup around, and
                without the city beside it there is nothing to say which of the
                two clocks it is on.
              */}
              {landsElsewhere && ` ${zoneCityLabel(endZone, locale)}`}
              {/* +1 when it lands on the next date, read in the arrival zone.
                  A red-eye that says "22:10 – 13:30" with no marker looks like
                  a fifteen-hour flight backwards. */}
              {landsNextDay && <span className="align-super text-xs"> +1</span>}
            </span>
          )}
        </p>

        {item.address && <p className="text-sm text-slate-500">{item.address}</p>}
        {item.addressLocal && item.addressLocal !== item.address && (
          // Shown to a driver. Kept visually distinct so it is findable at speed.
          <p lang="und" className="text-sm text-slate-700">
            {item.addressLocal}
          </p>
        )}

        {item.confirmationRef && (
          <p className="text-sm text-slate-500">
            {t('plan.confirmation')}:{' '}
            {/*
              Plain text, never linkified. This is the field most likely to look
              like a phone number — a policy number became a dialable link in a
              previous build.
            */}
            <code className="font-mono text-slate-900">{item.confirmationRef}</code>
          </p>
        )}

        <div className="text-sm text-slate-700">
          <Markdown>{item.notes}</Markdown>
        </div>

        {/*
          Map, phone, site. Each is 44px tall on a phone and inline text above
          it: these are the links a traveller taps while walking, one-handed,
          and they were 18px of underlined text sitting a few pixels apart.
        */}
        {(maps || item.phone || website) && (
          <div className="-mx-2 mt-0.5 flex flex-wrap items-center gap-x-1 text-sm sm:mx-0 sm:mt-1 sm:gap-x-4">
            {maps && (
              <a
                className="flex min-h-11 items-center px-2 text-slate-700 underline decoration-gold underline-offset-4 hover:text-slate-900 sm:min-h-0 sm:px-0"
                href={maps}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('plan.openMap')}
              </a>
            )}
            {item.phone && (
              <a
                className="flex min-h-11 items-center px-2 text-slate-700 underline decoration-gold underline-offset-4 hover:text-slate-900 sm:min-h-0 sm:px-0"
                href={`tel:${item.phone}`}
              >
                {t('plan.call')}
              </a>
            )}
            {website && (
              <a
                className="flex min-h-11 items-center px-2 text-slate-700 underline decoration-gold underline-offset-4 hover:text-slate-900 sm:min-h-0 sm:px-0"
                href={website}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {t('plan.website')}
              </a>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

export function PlanDaySection({
  day,
  index,
  timeZone,
  emergencyInfo,
  changes,
}: {
  day: PlanDayType
  index: number
  timeZone: string
  /** Repeated at the top of every day — see the note where it is rendered. */
  emergencyInfo: string | null
  /** See `ItemEntry`. Absent marks nothing, rather than asserting nothing moved. */
  changes?: PlanChanges | undefined
}) {
  const t = useT()
  const { locale } = useLocale()
  const empty = day.items.length === 0 && !day.notes?.trim()
  // The day's OWN change — its name or the paragraph above its entries — and
  // never a roll-up of the entries beneath it. A heading badge that lit up
  // because one booking moved would put a marker on nine days of a nine-day
  // trip the first time Ana did a pass over the whole itinerary.
  const dayChange = changes?.days.get(day.id)

  return (
    <section id={`day-${index}`} className="border-t border-slate-200 py-5 sm:py-6">
      {/*
        Sticky, so the day you are reading stays named.

        A nine-day itinerary is about 2,800px on a phone. Scrolled into the
        middle of it, every screenful looked the same and there was nothing
        saying which day it belonged to — so checking "is the museum on Tuesday
        or Wednesday?" meant scrolling up until a heading appeared, then back
        down. `top-0` with a background is the whole fix.
      */}
      <header className="sticky top-0 z-10 -mx-4 flex flex-wrap items-baseline gap-x-3 bg-white/95 px-4 py-2 backdrop-blur-sm sm:static sm:mx-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-gold/50 text-xs text-slate-500">
          {index + 1}
        </span>
        <h3 className="text-xl sm:text-2xl">
          {/*
            The day's name is the heading and its date is the subtitle, because
            "Versalhes" is what somebody remembers and "Friday 12 June" is what
            they check. Days had no name at all until the titles Ana wrote were
            recovered — see tools/migrate-trek/day-titles.json.
          */}
          {day.title || (
            <span className="first-letter:uppercase">{formatDayHeading(day.date, locale)}</span>
          )}
        </h3>
        {day.title && (
          <span className="text-sm text-slate-500 first-letter:uppercase">
            {formatDayHeading(day.date, locale)}
          </span>
        )}
        {dayChange && <ChangeBadge kind={dayChange} />}
      </header>

      {/*
        The emergency block, on every day rather than once at the top — and shut
        until it is wanted.

        Ana's own migrated notes ended every single day with a "CONTATO DE
        EMERGÊNCIA" section, which is the advisor telling us where it belongs:
        somebody looking for a consulate number on day six should not have to
        scroll back to day one, and every printed page needs it.

        It used to render open, as a red panel, on all nine — including the days
        with nothing else on them, which made a phone screen of "EMERGÊNCIA /
        consulate number / nothing here yet" repeated down the page. Nine alarms
        is no alarm. Shut it is one quiet line that is still the first thing
        under the date; `open` in print puts the full text on every page, which
        is the version that has to survive a dead battery.
      */}
      {emergencyInfo?.trim() && (
        <details className="print-open group mt-3">
          <summary className="label-caps flex min-h-11 cursor-pointer list-none items-center gap-2 text-red-800/80 marker:content-none hover:text-red-800 sm:min-h-0">
            <span aria-hidden="true">✚</span>
            {t('plan.emergency')}
            <span aria-hidden="true" className="text-[0.9em] text-red-800/50">
              {'▸'}
            </span>
          </summary>
          <div className="mt-1 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950">
            <Markdown>{emergencyInfo}</Markdown>
          </div>
        </details>
      )}

      {day.notes?.trim() && (
        <div className="mt-3 text-slate-700">
          <Markdown>{day.notes}</Markdown>
        </div>
      )}

      {!empty && (
        <ul className="mt-2 flex flex-col divide-y divide-slate-100">
          {day.items.map((item) => (
            <ItemEntry key={item.id} item={item} timeZone={timeZone} changes={changes} />
          ))}
        </ul>
      )}

      {empty && <p className="mt-2 text-sm text-slate-400">{t('plan.empty')}</p>}
    </section>
  )
}
